import { Gem, Sparkles, Zap, Rocket, Diamond, Crown, Award, Trophy, Star, Flame } from "lucide-react";

/**
 * 10 tier bands (5 levels each). Each band has a color palette + representative
 * gem icon. Level 0 renders as a muted placeholder ("NEW").
 */
const BANDS = [
  { name: "SPARK",    accent: "#B87333", accent2: "#F5A76A", Icon: Sparkles },
  { name: "SILVER",   accent: "#C0C0C0", accent2: "#E5E5E5", Icon: Star },
  { name: "GOLD",     accent: "#FFB800", accent2: "#FFDA6A", Icon: Award },
  { name: "PLATINUM", accent: "#B4E1FF", accent2: "#E3F4FF", Icon: Trophy },
  { name: "EMERALD",  accent: "#39FF14", accent2: "#8FFFC1", Icon: Gem },
  { name: "SAPPHIRE", accent: "#00A3FF", accent2: "#00FFFF", Icon: Zap },
  { name: "RUBY",     accent: "#FF3355", accent2: "#FF6699", Icon: Flame },
  { name: "AMETHYST", accent: "#A855F7", accent2: "#D0A9FF", Icon: Rocket },
  { name: "DIAMOND",  accent: "#E0FFFF", accent2: "#FFFFFF", Icon: Diamond },
  { name: "MYTHIC",   accent: "#FF007F", accent2: "#FFB800", Icon: Crown },
];

const bandFor = (level) => {
  if (!level || level < 1) return null;
  const idx = Math.min(9, Math.floor((level - 1) / 5));
  return { ...BANDS[idx], bandIndex: idx };
};

/**
 * <LevelBadge level={7} kind="gifter" size="md" />
 *
 * Sizes: sm (chip 22px), md (56px), lg (96px)
 * Kind: "gifter" | "buyer" — only affects the small caption suffix on md/lg.
 */
export default function LevelBadge({ level = 0, kind, size = "md", showLabel = true, className = "" }) {
  const band = bandFor(level);

  if (size === "sm") {
    // Compact chip — for inline user displays (comments, leaderboards)
    if (!band) return null;
    return (
      <span
        data-testid={`level-badge-sm-${level}`}
        className={`inline-flex items-center gap-1 px-1.5 py-0.5 font-mono text-[9px] font-bold uppercase tracking-widest ${className}`}
        style={{
          background: `${band.accent}18`,
          border: `1px solid ${band.accent}66`,
          color: band.accent,
          textShadow: `0 0 4px ${band.accent}88`,
        }}
        title={`Level ${level} · ${band.name}`}
      >
        <band.Icon className="h-2.5 w-2.5" strokeWidth={2.2} />
        L{level}
      </span>
    );
  }

  // md + lg use the same layout with different scale
  const dim = size === "lg" ? "h-24 w-24" : "h-14 w-14";
  const iconSize = size === "lg" ? "h-10 w-10" : "h-6 w-6";
  const numText = size === "lg" ? "text-2xl" : "text-base";

  if (!band) {
    // L0 — muted "new" placeholder
    return (
      <div
        data-testid={`level-badge-${size}-0`}
        className={`flex flex-col items-center gap-1 ${className}`}
      >
        <div
          className={`${dim} relative flex items-center justify-center border border-white/15 bg-black/50`}
          style={{ clipPath: "polygon(50% 0, 100% 25%, 100% 75%, 50% 100%, 0 75%, 0 25%)" }}
        >
          <span className={`font-display font-black text-white/40 ${numText}`}>0</span>
        </div>
        {showLabel && (
          <p className="font-mono text-[9px] uppercase tracking-widest text-white/30">
            unranked
          </p>
        )}
      </div>
    );
  }

  const suffix = kind === "buyer" ? "buyer" : kind === "gifter" ? "gifter" : "";

  return (
    <div
      data-testid={`level-badge-${size}-${level}`}
      className={`flex flex-col items-center gap-1 ${className}`}
    >
      {/* Hexagonal badge */}
      <div className={`${dim} relative flex items-center justify-center`}>
        {/* Outer glow */}
        <div
          className="absolute inset-0"
          style={{
            background: `radial-gradient(circle at center, ${band.accent}66 0%, transparent 65%)`,
            filter: "blur(4px)",
          }}
        />
        {/* Solid hex */}
        <div
          className="absolute inset-0"
          style={{
            background: `linear-gradient(135deg, ${band.accent} 0%, ${band.accent2} 50%, ${band.accent} 100%)`,
            clipPath: "polygon(50% 0, 100% 25%, 100% 75%, 50% 100%, 0 75%, 0 25%)",
            boxShadow: `0 0 20px ${band.accent}80, inset 0 0 15px rgba(0,0,0,0.4)`,
          }}
        />
        {/* Inner darker hex */}
        <div
          className="absolute inset-[10%] flex items-center justify-center flex-col gap-0"
          style={{
            background: "#0A0A0C",
            clipPath: "polygon(50% 0, 100% 25%, 100% 75%, 50% 100%, 0 75%, 0 25%)",
          }}
        >
          <band.Icon
            className={iconSize}
            strokeWidth={1.6}
            style={{
              color: band.accent,
              filter: `drop-shadow(0 0 6px ${band.accent})`,
            }}
          />
          <span
            className={`font-display font-black leading-none ${numText}`}
            style={{ color: band.accent2, textShadow: `0 0 8px ${band.accent}` }}
          >
            {level}
          </span>
        </div>
      </div>

      {showLabel && (
        <div className="flex flex-col items-center leading-none">
          <p
            className="font-display font-black text-[11px] tracking-widest uppercase"
            style={{ color: band.accent, textShadow: `0 0 6px ${band.accent}66` }}
          >
            {band.name}
          </p>
          {suffix && (
            <p className="font-mono text-[9px] uppercase tracking-widest text-white/40 mt-0.5">
              {suffix}
            </p>
          )}
        </div>
      )}
    </div>
  );
}

export { bandFor };
